import { getCellValue } from '../utils/cell-value'
import type { DataGrid, GridColumnInfo } from '../types'
import { getExportBlobByContent } from './import-export.util'

const EXPORT_MODE_KEYS: Record<string, string> = {
  current: 'z.export.modes.current',
  selected: 'z.export.modes.selected',
  all: 'z.export.modes.all',
  empty: 'z.export.modes.empty',
}

function resolveModeList(modes: string[], keyMap: Record<string, string>) {
  return modes.map((value) => ({
    value,
    label: keyMap[value] || `z.export.modes.${value}`,
  }))
}

function resolveTypeList(types: string[]) {
  return types.map((value) => ({ value, label: `z.export.types.${value}` }))
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

function buildCsv(columns: GridColumnInfo[], rows: Record<string, unknown>[]) {
  const escapeCsvCell = (value: unknown) => {
    const text = value == null ? '' : String(value)
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
  }
  const header = columns.map((c) => escapeCsvCell(c.title || c.field)).join(',')
  const body = rows.map((row) =>
    columns.map((c) => escapeCsvCell(getCellValue(row, c))).join(',')
  )
  return `\ufeff${[header, ...body].join('\n')}`
}

function defaultExportColumn(column: GridColumnInfo) {
  if (!column.field) return false
  const type = column.type || ''
  return !['seq', 'checkbox'].includes(type)
}

function formatDateFilename() {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`
}

export function useImportExport($grid: DataGrid) {
  const { reactData, internalData } = $grid

  const resolveExportColumns = () =>
    internalData.visibleColumns.filter(
      (c) => c.visible !== false && defaultExportColumn(c)
    )

  const resolveExportRows = (options: Record<string, unknown>) => {
    const mode = options.mode || 'current'
    if (mode === 'selected') return $grid.getAllCheckbox?.() || []
    if (mode === 'empty') return []
    return reactData.tableData
  }

  $grid.openExport = (options: Record<string, unknown> = {}) => {
    const columns = resolveExportColumns().map((c) => ({
      ...c,
      checked: true,
      halfChecked: false,
      disabled: false,
    }))
    const selectRecords = $grid.getAllCheckbox?.() || []
    Object.assign(reactData.exportStore, {
      columns,
      typeList: resolveTypeList(['csv', 'html', 'txt']),
      modeList: resolveModeList(['current', 'selected'], EXPORT_MODE_KEYS),
      hasFooter: !!reactData.footerTableData.length,
      hasMerge: false,
      hasTree: false,
      isPrint: false,
      hasColgroup: false,
      visible: true,
    })
    Object.assign(reactData.exportParams, {
      mode: selectRecords.length ? 'selected' : 'current',
      type: 'csv',
      filename: formatDateFilename(),
      sheetName: document.title || '',
      isHeader: true,
      isFooter: !!reactData.footerTableData.length,
      ...options,
    })
    reactData.initStore.export = true
  }

  $grid.closeExport = () => {
    reactData.exportStore.visible = false
  }

  $grid.openPrint = (options: Record<string, unknown> = {}) => {
    $grid.openExport?.(options)
    reactData.exportStore.isPrint = true
  }

  $grid.exportData = (
    options: Record<string, unknown> = {},
    override: Record<string, unknown> = {}
  ) => {
    const opts = { ...options, ...override }
    const expColumns =
      (opts.columns as GridColumnInfo[]) || resolveExportColumns()
    const rows = resolveExportRows(opts)
    const type = (opts.type as string) || 'csv'
    const filename = `${opts.filename || formatDateFilename()}.${type}`

    if (type === 'csv' || type === 'txt') {
      const content = buildCsv(expColumns, rows)
      const blob = getExportBlobByContent(content, {
        type: type === 'txt' ? 'plain' : 'csv',
      })
      if (opts.download !== false) downloadBlob(blob, filename)
      return Promise.resolve({ type, content, blob, filename })
    }

    if (type === 'html') {
      const header = expColumns
        .map((c) => `<th>${c.title || c.field}</th>`)
        .join('')
      const body = rows
        .map(
          (row) =>
            `<tr>${expColumns.map((c) => `<td>${getCellValue(row, c) ?? ''}</td>`).join('')}</tr>`
        )
        .join('')
      const content = `<table><thead><tr>${header}</tr></thead><tbody>${body}</tbody></table>`
      if (opts.download !== false) {
        downloadBlob(
          getExportBlobByContent(content, { type: 'html' }),
          filename
        )
      }
      return Promise.resolve({ type, content, filename })
    }

    const content = buildCsv(expColumns, rows)
    const blob = getExportBlobByContent(content, { type: 'csv' })
    if (opts.download !== false) {
      downloadBlob(blob, `${opts.filename || formatDateFilename()}.csv`)
    }
    return Promise.resolve({ type: 'csv', content, blob, filename })
  }

  $grid.print = (options: Record<string, unknown> = {}) =>
    $grid.exportData!({ ...options, type: 'html', download: false }).then(
      ({ content }: any) => {
        const w = window.open('', '_blank')
        if (w) {
          w.document.write(
            `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${options.sheetName || document.title}</title></head><body>${content}</body></html>`
          )
          w.document.close()
          w.focus()
          w.print()
          w.close()
        }
        return { html: content }
      }
    )
}
